import React, {useState, useEffect} from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  Grid,
  Container,
  Typography,
  LinearProgress,
  Box,
  Skeleton,
  FormControl,
  Autocomplete,
  Checkbox,
  Button,
  Dialog,
  DialogActions,
  DialogContent
} from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import { useGetDevicesQuery, useUserProfileQuery, useUpdateProcessMutation, useGetUserAccessQuery } from '../services/dvagent';
import { Toaster } from '../sections/@dashboard/attributes';
import { getToken, storeAdmin, removeToken } from '../services/LocalStorageService';
import Iconify from '../components/iconify';

function LinearProgressWithLabel(props) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress sx={{height:'15px'}} variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

function formatTimestamp(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
}

export default function Devices() {

  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [haveDeviceAppUpdateAccess, setHaveDeviceAppUpdateAccess] = useState(false);
  const [devices, setDevices] = useState([]);
  const [network, setNetwork] = useState([]);
  const [popupMsg, setPopupMsg] = useState("");
  const [updateProcess] = useUpdateProcessMutation();
  const { data:userData, isSuccess:userIsSuccess } = useUserProfileQuery(getToken());
  const { data, isSuccess, error } = useGetDevicesQuery({propertyId:location.state.property_id, networkId:location.state.network_id, token:getToken(), "location":location});
  const { data:userAccessData, isSuccess:userAccessIsSuccess, error:userAccessError } = useGetUserAccessQuery({"token":getToken(), "id":location.state.property_id, "location":location});
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [checkedRows, setCheckedRows] = useState([]);
  const paginationModel = { page: 0, pageSize: 10 };
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [openUpdateModel, setOpenUpdateModel] = useState(false);
  const [toasterData, setToasterData] = useState({
    trigger: false,
    status: true,
    symbol: '✔',
    message: '',
  });

  const handleClose = () => {
    setOpenUpdateModel(false);
  };

  if(userData && userIsSuccess) {
    storeAdmin((userData.data[0].is_superuser).toString());
  }
  
  const filteredData = devices.filter(
    (item) =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.serialNumber.toLowerCase().includes(searchText.toLowerCase()) ||
      item.osName.toLowerCase().includes(searchText.toLowerCase()) ||
      item.systemModel.toLowerCase().includes(searchText.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
  );


  const suggestions = Array.from(
    new Set(devices.flatMap((item) => item.tags.map((tag) => tag)))
  );
  suggestions.sort((a, b) => a.localeCompare(b));
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleUpdateProcess = async () => {
    setPopupMsg("");
    if(!haveDeviceAppUpdateAccess) {
      showToast(false, 'ⓘ', 'Access Denied');
      return
    }
    const actualData = {
      propertyId: location.state.property_id,
      networkId: location.state.network_id,
      id : 0,
      token: getToken()
    };
    try {
      if(checkedRows.length!==0) {
        try{
          if (!network.appId || !network.appIdentifier || !network.tag_dv || !network.tag_maintenance || !network.version) {
            setPopupMsg("Some required fields are missing. Please make sure the property network information is complete.");
            setOpenUpdateModel(true);
            return;
          }
        } catch(error) {
          console.log(error)
        }
        const res = await updateProcess(actualData);
        if (res.data && res.data.process.length) {
          let maxIdObject = res.data.process[0];
          for (let i = 1; i < res.data.process.length; i+=1) {
            if (res.data.process[i].id > maxIdObject.id) {
              maxIdObject = res.data.process[i];
            }
          }
          if (maxIdObject.data.length){
            let flag = false;
            for (let i = 0; i < maxIdObject.data.length; i+=1) {
              const item = maxIdObject.data[i];
              if (item.status === 'In Progress...') {
                flag = true;
                break;
              }
            }
            if (flag && checkedRows.length!==0) {
              setPopupMsg("Sorry, you'll need to wait until the ongoing iPad app update process is complete before scheduling another.");
              setOpenUpdateModel(true);
            } else {
              const nextState = { location, checkedRows };
              navigate('/cloud/device-app-update', { state: nextState });
            }
          } else {
            const nextState = { location, checkedRows };
            navigate('/cloud/device-app-update', { state: nextState });
          }
        } else {
          const nextState = { location, checkedRows };
          navigate('/cloud/device-app-update', { state: nextState });
        }
      } else {
        const nextState = { location, checkedRows };
        navigate('/cloud/device-app-update', { state: nextState });
      }
    } catch (e) {
      // setDevices([])
    }
  };

  const handleCheckboxChange = (event, serialNumber) => {
    setPopupMsg("");
    if (event.target.checked) {
      if (checkedRows.length < 100) {
        setCheckedRows([...checkedRows, serialNumber]);
      } else {
        event.target.checked = false;
        setPopupMsg("You can select a maximum of 100 devices.");
        setOpenUpdateModel(true);
      }
    } else {
      setIsAllSelected(false);
      setCheckedRows(checkedRows.filter((id) => id !== serialNumber));
    }
  };

  const handleSelectAll = (event) => {
    setPopupMsg("");
    const allSerialNumbers = filteredData.map(row => row);
    if (event.target.checked) {
      if (allSerialNumbers.length > 100) {
        const device = filteredData.slice(0, 100).map(row => row);
        setCheckedRows(device);
        setIsAllSelected(true);
        setPopupMsg("You can select a maximum of 100 devices.");
        setOpenUpdateModel(true);
        return;
      }
      setIsAllSelected(true);
      setCheckedRows(allSerialNumbers);
    } else {
      setIsAllSelected(false);
      setCheckedRows([]);
    }
  };

  useEffect(() => {
    try {
      setHaveDeviceAppUpdateAccess(userAccessData.data.have_device_app_update_access);
    } catch(err) {
      console.log(err)
    }
    try{
      if (error) {
        if (error.status === 401) {
          showToast(false, 'ⓘ', 'Access Denied');
        } else {
          showToast(false, 'ⓘ', 'Something went wrong, server error');
        }
      }
      if (data && isSuccess) {
        setDevices(data.devices);
        try{
          setNetwork(data.network[0]);
        } catch(error) {
          console.log(error)
        }
        setLoading(true);
      }
    } catch(err){
      console.log(err)
    }
    // console.log(userAccessData)
  }, [data, isSuccess, error]);  


  const handleExportToExcel = () => {
    const data = filteredData.map((item, index) => ({
      "S. No.": index + 1,
      "Name": item.name,
      "Serial Number": item.serialNumber,
      "Id": item.id,
      "IP Address": item.ip,
      "MAC Address": item.wifiMac,
      "OS Version": item.osName,
      "Model": item.systemModel,
      "Device": item.systemType,
      "Single App Mode": item.kioskAppName,
      "Location": item.location
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Devices");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(excelBlob);
    link.download = 'devices.xlsx';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

// Function to handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (sortConfig.key) {
      return [...filteredData].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return filteredData;
  }, [filteredData, sortConfig]);

  const showToast = (status, symbol, message) => {
    setToasterData({
      trigger: true,
      status,
      symbol,
      message,
    });

    setTimeout(() => {
      setToasterData((prev) => ({
        ...prev,
        trigger: false,
      }));
    }, 5000); 
  };

  return (
    <>
      <div>
          <Dialog
            open={openUpdateModel}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            // maxWidth="xl" 
          >
          <DialogContent>
            {popupMsg}
          </DialogContent>
          <DialogActions>
              <Button onClick={handleClose} autoFocus>
                  Close
              </Button>
          </DialogActions>
          </Dialog>
      </div>

      <Helmet>
        <title> DV Agent </title>
      </Helmet>

      {toasterData.trigger && (
        <Toaster
          trigger={toasterData.trigger}
          status={toasterData.status}
          symbol={toasterData.symbol}
          message={toasterData.message}
        />
      )}

      <Container maxWidth="xxl">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Box to="/" component={RouterLink}>
            <img width={40} src="../../assets/back-button.gif" alt="verified" style={{marginLeft :4, display: 'inline-block', verticalAlign: 'middle' }} />
          </Box>
          <Typography
            gutterBottom
            variant=""
            component="div"
            sx={{
              color: '#A8A196',
              textAlign: 'left',
              marginLeft: 2,
              paddingBottom: 1,
              fontSize: '1.5rem',
              fontWeight: 'bold',
            }}
          >
            {location.state.name} 
            <Typography variant="" component="div" sx={{ fontSize: 16, color: '#4D4D4D' }}>
              {location.state.network_name}
            </Typography>
          </Typography>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={7} md={6} xl={6} style={{ display: 'flex' }}>
            <FormControl  fullWidth>
              <Autocomplete
                freeSolo
                options={suggestions}
                inputValue={searchText}
                onInputChange={(e, newInputValue) => setSearchText(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Name/Serial/Floor"
                    variant="outlined"
                    size="small"
                  />
                )}
                sx={{marginRight:'10px'}}
              />
            </FormControl>
              <FormControl>
                <Button
                  onClick={handleUpdateProcess}
                  sx={{
                    color: '#FFF',
                    border: 1,
                    borderColor: '#A8A196',
                    bgcolor: '#A8A196',
                    paddingX: 3,
                    ":hover": {
                      bgcolor: "#7D7463",
                      borderColor: '#7D7463',
                    }
                  }}
                >
                  {checkedRows >= 0 ? <>View&nbsp;Activity</> : <>Update&nbsp;App</>}
                </Button>
              </FormControl>
          </Grid>

          <Grid item xs={12} sm={5} md={6} xl={6}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Grid>
        </Grid>

        <div>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#A8A196" }}>
                    <Checkbox
                      onChange={(e) => handleSelectAll(e)}
                      checked={isAllSelected}
                    />
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196" }}>
                    S.&nbsp;No.
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('name')}>
                    &nbsp;&nbsp;&nbsp;&nbsp;Name&nbsp;&nbsp;&nbsp;&nbsp;
                    {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('serialNumber')}>
                    Serial&nbsp;Number
                    {sortConfig.key === 'serialNumber' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }} onClick={() => handleSort('osName')}>
                    OS
                    {sortConfig.key === 'osName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }} onClick={() => handleSort('systemModel')}>
                    Model
                    {sortConfig.key === 'systemModel' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }} onClick={() => handleSort('lastConnected')}>
                    Last&nbsp;MDM&nbsp;Checkin
                    {sortConfig.key === 'lastConnected' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }} onClick={() => handleSort('ssid')}>
                    WiFi&nbsp;SSID
                    {sortConfig.key === 'ssid' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }} onClick={() => handleSort('kioskAppName')}>
                    Single&nbsp;App
                    {sortConfig.key === 'kioskAppName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </TableCell>

                  <TableCell sx={{ color: "#A8A196", textAlign: "center" }}>Battery&nbsp;Percentage</TableCell>
                </TableRow>
              </TableHead>
              {loading ? (
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No Device Found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            <Checkbox
                              onChange={(e) => handleCheckboxChange(e, row)}
                              checked={checkedRows.includes(row)}
                            />
                          </TableCell>
                          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                          <TableCell
                            to="/cloud/device-profile"
                            state={{ row, location }}
                            component={RouterLink}
                            style={{
                              textDecoration: 'none',
                              color: '#A8A196',
                              fontWeight: 'bold',
                              textAlign: "center",
                            }}
                          >
                            {row.name}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>{row.serialNumber}</TableCell>
                          <TableCell sx={{ textAlign: "center" }}>{row.osName}</TableCell>
                          <TableCell sx={{ textAlign: "center" }}>{row.systemModel}</TableCell>
                          <TableCell sx={{ textAlign: "center" }}>{formatTimestamp(row.lastConnected)}</TableCell>
                          <TableCell sx={{ textAlign: "center" }}>{row.ssid}</TableCell>
                          {row.kioskAppName ? (
                            <TableCell sx={{ color: '#96C291', fontWeight: 'bold', textAlign: "center" }}>
                              Lock&nbsp;({row.kioskAppName})
                            </TableCell>
                          ) : (
                            <TableCell sx={{ color: '#FFB7B7', fontWeight: 'bold', textAlign: "center" }}>
                              Unlock
                            </TableCell>
                          )}
                          <TableCell>
                            <LinearProgressWithLabel value={parseFloat(row.batteryEstCharge)} />
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              ):(
                <TableBody>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                      <TableCell><Skeleton animation="wave" height={25} width="100%" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={2} sm={2} md={2} xl={2}>
              <Button
                component="label"
                variant="contained"
                onClick={handleExportToExcel}
                sx={{
                  color: '#FFF',
                  border: 1,
                  borderColor: '#A8A196',
                  bgcolor: '#A8A196',
                  ":hover": {
                    bgcolor: "#7D7463",
                    borderColor: '#7D7463',
                  },
                }}
              >
                <Iconify icon='teenyicons:download-solid' />&nbsp;&nbsp;Export
              </Button>
            </Grid>
            <Grid item xs={0} sm={10} md={10} xl={10}>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Grid>
        </div>
      </Container>
    </>
  );
}